import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { DocumentService } from './document.service';
import { resolveDocumentStatutLabel } from './document-avoir-row.util';
import { parseConvertedChildrenFromApiResponse } from './document-conversion-lock.util';
import {
    buildDocumentPdfPriceContext,
    calculateDocumentPdfTotals,
} from './document-pdf-totals.util';
import { stockTransfertIsReceptionLabel } from '../logiciel-caisse/logiciel-caisse-stock-transfert-map.util';

export interface DocumentLieRow {
    id?: number;
    type?: string;
    reference?: string;
    date?: string;
    montant?: number | string;
    statut?: string;
    _lcWorkflowLink?: { path: string; queryParams?: Record<string, string> };
}

export type DocumentsLieSkeletonRow = DocumentLieRow & {
    erpDocumentId?: number | null;
    lcTransfertId?: number | null;
};

function resolveErpDocumentIdForSkeleton(row: DocumentsLieSkeletonRow): number | null {
    const direct = Number(row.erpDocumentId);
    if (Number.isFinite(direct) && direct > 0) {
        return direct;
    }
    const fromLink = Number(row._lcWorkflowLink?.queryParams?.['id']);
    if (Number.isFinite(fromLink) && fromLink > 0) {
        return fromLink;
    }
    const rowId = Number(row.id);
    return Number.isFinite(rowId) && rowId > 0 ? rowId : null;
}

/** Complète date / montant / statut depuis ERP (et optionnellement LC). */
export function enrichDocumentsLieSkeletonRows$(
    documentService: DocumentService,
    rows: DocumentsLieSkeletonRow[],
    lcStockTransfertService?: {
        getById: (id: number) => import('rxjs').Observable<{
            date_transfert?: string;
            created_at?: string;
            is_reception?: number;
            circulation_cree?: boolean;
        } | null>;
    } | null
): Observable<DocumentLieRow[]> {
    if (!rows.length) {
        return of([]);
    }
    const erpIds = [
        ...new Set(
            rows
                .map((r) => resolveErpDocumentIdForSkeleton(r))
                .filter((id): id is number => id != null && id > 0)
        ),
    ];
    const erpLoads$ = erpIds.length
        ? forkJoin(
              erpIds.map((id) =>
                  documentService.getDocumentById(id).pipe(
                      map((res) => ({
                          id,
                          doc: unwrapDocumentApiPayload(res),
                      })),
                      catchError(() => of({ id, doc: null as unknown }))
                  )
              )
          )
        : of([] as { id: number; doc: unknown }[]);

    return erpLoads$.pipe(
        switchMap((erpResults) => {
            const erpById = new Map<number, unknown>();
            for (const { id, doc } of erpResults) {
                if (doc) {
                    erpById.set(id, doc);
                }
            }
            const lcIds = [
                ...new Set(
                    rows
                        .map((r) => Number(r.lcTransfertId))
                        .filter((id) => Number.isFinite(id) && id > 0)
                ),
            ];
            if (!lcIds.length || !lcStockTransfertService) {
                return of({ erpById, lcById: new Map<number, unknown>() });
            }
            return forkJoin(
                lcIds.map((id) =>
                    lcStockTransfertService.getById(id).pipe(catchError(() => of(null)))
                )
            ).pipe(
                map((lcDetails) => {
                    const lcById = new Map<number, unknown>();
                    lcIds.forEach((id, index) => {
                        if (lcDetails[index]) {
                            lcById.set(id, lcDetails[index]);
                        }
                    });
                    return { erpById, lcById };
                })
            );
        }),
        map(({ erpById, lcById }) =>
            rows.map((row) => {
                const base: DocumentLieRow = {
                    type: row.type,
                    reference: row.reference,
                    date: row.date,
                    montant: row.montant,
                    statut: row.statut,
                    _lcWorkflowLink: row._lcWorkflowLink,
                    id: row.id,
                };
                const erpId = resolveErpDocumentIdForSkeleton(row);
                if (erpId != null && erpById.has(erpId)) {
                    const lie = mapDocumentToLieRow(erpById.get(erpId));
                    return {
                        ...lie,
                        type: row.type || lie.type,
                        reference: row.reference || lie.reference,
                        _lcWorkflowLink: row._lcWorkflowLink ?? undefined,
                    };
                }
                const lcId = Number(row.lcTransfertId);
                if (Number.isFinite(lcId) && lcId > 0 && lcById.has(lcId)) {
                    const lc = lcById.get(lcId) as {
                        date_transfert?: string;
                        created_at?: string;
                        is_reception?: number;
                        circulation_cree?: boolean;
                    };
                    return {
                        ...base,
                        id: lcId,
                        date: lc?.date_transfert ?? lc?.created_at ?? base.date ?? '--',
                        montant: 0,
                        statut: stockTransfertIsReceptionLabel(
                            lc?.is_reception,
                            lc?.circulation_cree
                        ),
                    };
                }
                return base;
            })
        )
    );
}

const DOCUMENT_TYPE_LABELS: Record<number, string> = {
    7: 'Devis',
    8: 'Commande',
    11: 'Facture',
    10: 'Bon de livraison',
    9: 'Bon de sortie',
    12: 'Commande fournisseur',
    13: 'Bon de réception',
    14: 'Facture fournisseur',
    15: 'Inventaire',
    16: 'Bon de transfert',
    17: 'Inventaire',
    18: 'Retour fournisseur',
    19: 'Avoir Fournisseur',
    20: "PV d'écart",
};

export function unwrapDocumentApiPayload(response: any): any {
    const doc =
        response?.data?.document ??
        response?.data ??
        response?.document ??
        response;
    return doc && typeof doc === 'object' && doc.id != null ? doc : null;
}

function lignesFromDocument(doc: any): any[] {
    if (!doc) {
        return [];
    }
    const raw =
        doc.lignesDocument ??
        doc.lignes_document ??
        doc.document?.lignesDocument ??
        doc.document?.lignes_document;
    return Array.isArray(raw) ? raw : [];
}

function headerAmount(doc: any, key: string): number {
    const n = parseFloat(String(doc?.[key] ?? '').replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
}

/** Montant affiché dans « Documents liés » (en-tête ou somme des lignes si en-tête à 0). */
export function resolveDocumentLinkedMontant(doc: any): number {
    if (!doc) {
        return 0;
    }
    const net = headerAmount(doc, 'net_payer');
    const ttc = headerAmount(doc, 'total_ttc');
    if (net !== 0) {
        return net;
    }
    if (ttc !== 0) {
        return ttc;
    }

    const lignes = lignesFromDocument(doc);
    if (lignes.length > 0) {
        const priceCtx = buildDocumentPdfPriceContext(doc);
        const totals = calculateDocumentPdfTotals(doc, lignes, priceCtx);
        if (totals.netAPayer !== 0) {
            return totals.netAPayer;
        }
        if (totals.totalTTC !== 0) {
            return totals.totalTTC;
        }
    }

    return net || ttc || 0;
}

export function getDocumentTypeLabelForLies(typeId: number | null | undefined, doc?: any): string {
    const id = typeId != null ? Number(typeId) : NaN;
    if (Number.isFinite(id) && DOCUMENT_TYPE_LABELS[id]) {
        return DOCUMENT_TYPE_LABELS[id];
    }
    return (
        doc?.typeDocument?.titre ||
        doc?.type_document?.titre ||
        'Document'
    );
}

export function mapDocumentToLieRow(doc: any): DocumentLieRow {
    const typeId = doc?.type_document_id != null ? Number(doc.type_document_id) : null;
    return {
        id: doc.id,
        type: getDocumentTypeLabelForLies(typeId, doc),
        reference: doc.ref_externe || doc.reference || '--',
        date: doc.date || doc.date_document || doc.created_at || '--',
        montant: resolveDocumentLinkedMontant(doc),
        statut: resolveDocumentStatutLabel(
            doc.statut || doc.statutDocument || doc.statut_data,
            doc.statut_id
        ),
    };
}

/**
 * Documents du même workflow (parent + conversions) pour l’onglet « Documents liés ».
 */
export function loadWorkflowDocumentsLies(
    documentService: DocumentService,
    documentData: any
): Observable<DocumentLieRow[]> {
    if (!documentData?.id) {
        return of([]);
    }

    const currentId = Number(documentData.id);
    const parentIdRaw =
        documentData.document_original_id ?? (documentData as any).document_original_id;
    const parentId =
        parentIdRaw != null && Number.isFinite(Number(parentIdRaw)) && Number(parentIdRaw) > 0
            ? Number(parentIdRaw)
            : null;

    const childrenSourceId = parentId ?? currentId;

    const children$ = documentService.getDocumentsByOriginalId(childrenSourceId).pipe(
        catchError(() => of(null))
    );

    const parent$ = parentId
        ? documentService.getDocumentById(parentId).pipe(catchError(() => of(null)))
        : of(null);

    return forkJoin({ parentRes: parent$, childrenRes: children$ }).pipe(
        switchMap(({ parentRes, childrenRes }) => {
            const relatedIds = new Set<number>();
            const parentDoc = parentId ? unwrapDocumentApiPayload(parentRes) : null;
            if (parentDoc?.id && Number(parentDoc.id) !== currentId) {
                relatedIds.add(Number(parentDoc.id));
            }

            const childSummaries = parseConvertedChildrenFromApiResponse(childrenRes);
            childSummaries.forEach((c) => {
                const cid = Number(c?.id);
                if (Number.isFinite(cid) && cid > 0 && cid !== currentId) {
                    relatedIds.add(cid);
                }
            });

            if (relatedIds.size === 0) {
                return of([] as DocumentLieRow[]);
            }

            const fetches = Array.from(relatedIds).map((id) =>
                documentService.getDocumentById(id).pipe(
                    map((res) => unwrapDocumentApiPayload(res)),
                    catchError(() => of(null))
                )
            );

            return forkJoin(fetches).pipe(
                map((docs) =>
                    docs
                        .filter((d): d is Record<string, unknown> => !!d && d.id != null)
                        .sort((a, b) => Number(a.id) - Number(b.id))
                        .map((d) => mapDocumentToLieRow(d))
                )
            );
        })
    );
}
